import axios from 'axios'
import type { ReminderPreferences, FinanceCsvPreviewResult, FinanceCsvApplyResult, Debt, CreateDebtData, PayoffPlan, NetWorthCurrent, NetWorthHistory } from '@/types'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (name: string, email: string, password: string, inviteCode?: string) =>
    api.post('/auth/register', { name, email, password, invite_code: inviteCode }),
  me: () => api.get('/auth/me'),
  dismissHouseholdPrompt: () => api.post('/auth/dismiss-household-prompt'),
}

// Bills
export const billsApi = {
  getAll: (params?: { status?: string; category_id?: number }) =>
    api.get('/bills', { params }),
  getById: (id: number) => api.get(`/bills/${id}`),
  create: (data: CreateBillData) => api.post('/bills', data),
  update: (id: number, data: Partial<CreateBillData>) =>
    api.put(`/bills/${id}`, data),
  delete: (id: number) => api.delete(`/bills/${id}`),
  markPaid: (id: number, data: MarkPaidData) =>
    api.post(`/bills/${id}/pay`, data),
  bulkPay: (bill_ids: number[], payment_date: string, confirmation_number?: string) =>
    api.post('/payments/bulk-pay', { bill_ids, payment_date, confirmation_number }),
  getForecast: (months?: number) =>
    api.get('/bills/forecast', { params: { months } }),
}

// Categories
export const categoriesApi = {
  getAll: (type?: 'bill' | 'expense' | 'income') =>
    api.get('/categories', { params: type ? { type } : {} }),
  getById: (id: number) => api.get(`/categories/${id}`),
  create: (data: CreateCategoryData) => api.post('/categories', data),
  update: (id: number, data: Partial<CreateCategoryData>) =>
    api.put(`/categories/${id}`, data),
  delete: (id: number) => api.delete(`/categories/${id}`),
}

// Transactions
export const transactionsApi = {
  getAll: (params?: TransactionParams) => api.get('/transactions', { params }),
  getById: (id: number) => api.get(`/transactions/${id}`),
  create: (data: CreateTransactionData) => api.post('/transactions', data),
  update: (id: number, data: Partial<CreateTransactionData>) =>
    api.put(`/transactions/${id}`, data),
  delete: (id: number) => api.delete(`/transactions/${id}`),
  linkBill: (id: number, data: { bill_id?: number; new_bill?: CreateBillData }) =>
    api.post(`/transactions/${id}/link-bill`, data),
  unlinkBill: (id: number) => api.delete(`/transactions/${id}/link-bill`),
  getSpending: (params?: { month?: string; start_date?: string; end_date?: string }) =>
    api.get('/transactions/spending', { params }),
  getIncome: (params?: { month?: string; start_date?: string; end_date?: string }) =>
    api.get('/transactions/income', { params }),
  getSummary: (params?: { start_date?: string; end_date?: string }) =>
    api.get('/transactions/spending', { params }),
  getCashFlow: (months?: number) =>
    api.get('/transactions/cashflow', { params: { months } }).then(r => r.data),
}

// SimpleFIN
export const simplefinApi = {
  getStatus: () => api.get('/simplefin/status'),
  getConfig: () => api.get('/simplefin/config'),
  setConfig: (accessUrl: string) => api.post('/simplefin/config', { access_url: accessUrl }),
  deleteConfig: () => api.delete('/simplefin/config'),
  getAccounts: () => api.get('/simplefin/accounts'),
  getPreview: () => api.get('/simplefin/preview'),
  syncAccounts: () => api.post('/simplefin/sync/accounts'),
  syncTransactions: (days?: number) =>
    api.post('/simplefin/sync/transactions', { days }),
  fullSync: (days?: number) => api.post('/simplefin/sync', { days }),
}

// Payments
export const paymentsApi = {
  getAll: (params?: { bill_id?: number; status?: string }) =>
    api.get('/payments', { params }),
  delete: (id: number) => api.delete(`/payments/${id}`),
}

// Email Parsing
export const emailApi = {
  getConfig: () => api.get('/email/config'),
  updateConfig: (data: EmailConfigData) => api.put('/email/config', data),
  testConnection: () => api.post('/email/test'),
  checkEmails: () => api.post('/email/check'),
  getTemplates: () => api.get('/email/templates'),
  getParsers: () => api.get('/email/parsers'),
  createParser: (data: CreateEmailParserData) => api.post('/email/parsers', data),
  updateParser: (id: number, data: Partial<CreateEmailParserData>) =>
    api.put(`/email/parsers/${id}`, data),
  deleteParser: (id: number) => api.delete(`/email/parsers/${id}`),
  getParsedEmails: (status?: string) =>
    api.get('/email/parsed', { params: status ? { status } : {} }),
  applyParsedEmail: (id: number) => api.post(`/email/parsed/${id}/apply`),
  ignoreParsedEmail: (id: number) => api.post(`/email/parsed/${id}/ignore`),
  deleteParsedEmail: (id: number) => api.delete(`/email/parsed/${id}`),
  // OAuth
  getOAuthStatus: () => api.get('/email/oauth/status'),
  disconnectOAuth: () => api.post('/email/oauth/disconnect'),
}

// Admin
export const adminApi = {
  getUsers: (status?: string) =>
    api.get('/admin/users', { params: status ? { status } : {} }),
  getPendingUsers: () => api.get('/admin/pending-users'),
  approveUser: (id: number) => api.put(`/admin/users/${id}/approve`),
  rejectUser: (id: number) => api.put(`/admin/users/${id}/reject`),
  deleteUser: (id: number) => api.delete(`/admin/users/${id}`),
}

// Scheduler
export const schedulerApi = {
  getStatus: () => api.get('/scheduler/status'),
  update: (type: 'email' | 'transactions', data: { enabled: boolean; interval?: number }) =>
    api.put(`/scheduler/${type}`, data),
  runNow: (type: 'email' | 'transactions') => api.post(`/scheduler/${type}/run`),
}

// Notifications
export const notificationsApi = {
  getAll: (includeRead?: boolean) =>
    api.get('/notifications', { params: includeRead ? { includeRead: 'true' } : {} }),
  markRead: (id: number) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id: number) => api.delete(`/notifications/${id}`),
  clearRead: () => api.delete('/notifications'),
}

// Households
export const householdsApi = {
  getMine: () => api.get('/households/mine'),
  create: (name: string) => api.post('/households', { name }),
  update: (id: number, data: { name: string }) => api.put(`/households/${id}`, data),
  delete: (id: number) => api.delete(`/households/${id}`),
  getMembers: (id: number) => api.get(`/households/${id}/members`),
  invite: (id: number, email: string) => api.post(`/households/${id}/invite`, { email }),
  removeMember: (id: number, userId: number) => api.delete(`/households/${id}/members/${userId}`),
  leave: (id: number) => api.post(`/households/${id}/leave`),
  joinByCode: (code: string) => api.post(`/households/join/${code}`),
  regenerateCode: (id: number) => api.post(`/households/${id}/regenerate-code`),
  transferAdmin: (id: number, userId: number) => api.post(`/households/${id}/transfer-admin/${userId}`),
  getInvitations: (id: number) => api.get(`/households/${id}/invitations`),
  cancelInvitation: (householdId: number, inviteId: number) =>
    api.delete(`/households/${householdId}/invitations/${inviteId}`),
  // Pending members (admin only)
  getPendingMembers: (id: number) => api.get(`/households/${id}/pending-members`),
  approveMember: (id: number, userId: number) => api.put(`/households/${id}/members/${userId}/approve`),
  rejectMember: (id: number, userId: number) => api.put(`/households/${id}/members/${userId}/reject`),
}

// Invitations
export const invitationsApi = {
  getPending: () => api.get('/households/invitations/pending'),
  accept: (id: number) => api.post(`/households/invitations/${id}/accept`),
  decline: (id: number) => api.post(`/households/invitations/${id}/decline`),
}

// Types
export interface CreateBillData {
  name: string
  amount: number
  due_day: number
  frequency: string
  category_id?: number
  autopay?: boolean
  url?: string
  notes?: string
  merchant_pattern?: string
}

export interface MarkPaidData {
  payment_date: string
  confirmation_number?: string
  amount_paid?: number
}

export interface CreateCategoryData {
  name: string
  icon?: string
  color?: string
  type: 'bill' | 'expense' | 'income'
  budget_amount?: number
}

export interface CreateTransactionData {
  merchant: string
  amount: number
  date: string
  category_id?: number
  bank_account_id?: number
  notes?: string
  type?: 'income' | 'expense'
  tax_deductible?: boolean
  tax_category?: string | null
}

export interface TransactionParams {
  start_date?: string
  end_date?: string
  category_id?: number
  bank_account_id?: number
  uncategorized?: boolean
  search?: string
  min_amount?: number
  max_amount?: number
  type?: 'income' | 'expense'
  tax_deductible?: boolean
  limit?: number
  offset?: number
}

export interface EmailConfigData {
  imap_host: string
  imap_port?: number
  imap_user: string
  imap_password?: string
  active?: boolean
}

export interface CreateEmailParserData {
  bill_id: number
  name?: string
  sender_pattern: string
  amount_pattern?: string
  due_date_pattern?: string
  subject_pattern?: string
  template?: string
  active?: boolean
}

export interface CreateSavingsGoalData {
  name: string
  target_amount: number
  current_amount?: number
  target_date?: string | null
  bank_account_id?: number | null
  color?: string
  icon?: string
}

// GitHub Issues
export const issuesApi = {
  create: (data: { title: string; description: string; type: 'bug' | 'feature' | 'question' }) =>
    api.post<{ success: boolean; issueNumber: number; issueUrl: string }>('/issues', data),
}

// Anomaly Alerts
export const anomalyAlertsApi = {
  getAll: (includeDismissed?: boolean) =>
    api.get('/anomaly-alerts', { params: includeDismissed ? { includeDismissed: 'true' } : {} }),
  getCount: () => api.get('/anomaly-alerts/count'),
  dismiss: (id: number) => api.post(`/anomaly-alerts/${id}/dismiss`),
  analyze: (days?: number) => api.post('/anomaly-alerts/analyze', null, { params: days ? { days } : {} }),
}

// Webhooks
export const webhooksApi = {
  getAll: () => api.get('/webhooks'),
  getEvents: () => api.get('/webhooks/events'),
  getById: (id: number) => api.get(`/webhooks/${id}`),
  create: (data: { name: string; url: string; events: string[]; active?: boolean }) =>
    api.post('/webhooks', data),
  update: (id: number, data: Partial<{ name: string; url: string; events: string[]; active: boolean }>) =>
    api.put(`/webhooks/${id}`, data),
  delete: (id: number) => api.delete(`/webhooks/${id}`),
  regenerateSecret: (id: number) => api.post(`/webhooks/${id}/regenerate-secret`),
  test: (id: number) => api.post(`/webhooks/${id}/test`),
  getLogs: (id: number, limit?: number) =>
    api.get(`/webhooks/${id}/logs`, { params: limit ? { limit } : {} }),
}

// Import
export const importApi = {
  transactionsCsv: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/import/transactions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  previewCsv: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/import/transactions/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  financePreview: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<FinanceCsvPreviewResult>('/import/finance-csv/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  financeApply: (file: File, skipDuplicates = true) => {
    const form = new FormData()
    form.append('file', file)
    form.append('skipDuplicates', String(skipDuplicates))
    return api.post<FinanceCsvApplyResult>('/import/finance-csv/apply', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// Export
export const exportApi = {
  transactionsCsv: (params?: { startDate?: string; endDate?: string; categoryId?: number }) =>
    api.get('/export/transactions/csv', { params, responseType: 'blob' }),
  billsCsv: () => api.get('/export/bills/csv', { responseType: 'blob' }),
  categoriesCsv: () => api.get('/export/categories/csv', { responseType: 'blob' }),
  reportPdf: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/export/report/pdf', { params, responseType: 'blob' }),
  getTaxCsv: (year: number) =>
    api.get('/export/tax', { params: { year, format: 'csv' }, responseType: 'blob' }),
  getTaxPdf: (year: number) =>
    api.get('/export/tax', { params: { year, format: 'pdf' }, responseType: 'blob' }),
}

// Savings Goals
export const savingsGoalsApi = {
  getAll: () => api.get('/savings-goals').then(r => r.data),
  create: (data: CreateSavingsGoalData) => api.post('/savings-goals', data).then(r => r.data),
  update: (id: number, data: Partial<CreateSavingsGoalData> & { current_amount?: number }) =>
    api.put(`/savings-goals/${id}`, data).then(r => r.data),
  delete: (id: number) => api.delete(`/savings-goals/${id}`).then(r => r.data),
}

// Debts
export const debtsApi = {
  getAll: () => api.get<Debt[]>('/debts'),
  getById: (id: number) => api.get<Debt>(`/debts/${id}`),
  create: (data: CreateDebtData) => api.post<Debt>('/debts', data),
  update: (id: number, data: Partial<CreateDebtData> & { paid_off_at?: string | null }) =>
    api.put<Debt>(`/debts/${id}`, data),
  delete: (id: number) => api.delete(`/debts/${id}`),
  getPayoffPlan: (method: 'snowball' | 'avalanche', extraPayment: number) =>
    api.get<PayoffPlan>('/debts/payoff-plan', {
      params: { method, extra_payment: extraPayment },
    }),
}

// Net Worth
export const netWorthApi = {
  getCurrent: () => api.get<NetWorthCurrent>('/net-worth/current'),
  getHistory: (months: number = 12) =>
    api.get<NetWorthHistory>('/net-worth/history', { params: { months } }),
  takeSnapshot: () => api.post('/net-worth/snapshot'),
}

// Reminders
export const remindersApi = {
  getPreferences: () =>
    api.get<ReminderPreferences>('/reminders/preferences'),
  updatePreferences: (data: Partial<Pick<ReminderPreferences, 'enabled' | 'days_before' | 'send_overdue'>>) =>
    api.put<ReminderPreferences>('/reminders/preferences', data),
}

// Merchant Patterns
export interface MerchantPattern {
  id: number
  category_id: number
  pattern: string
  min_amount: number | null
  max_amount: number | null
  household_id: number | null
  category_name: string
  category_type: string
  category_icon?: string
  category_color?: string
}

export interface PatternTestResult {
  description: string
  match: MerchantPattern | null
}

export const merchantPatternsApi = {
  list: () => api.get<MerchantPattern[]>('/merchant-patterns').then(r => r.data),
  create: (data: { category_id: number; pattern: string; min_amount?: number | null; max_amount?: number | null }) =>
    api.post<MerchantPattern>('/merchant-patterns', data).then(r => r.data),
  update: (id: number, data: Partial<{ category_id: number; pattern: string; min_amount: number | null; max_amount: number | null }>) =>
    api.put<MerchantPattern>(`/merchant-patterns/${id}`, data).then(r => r.data),
  delete: (id: number) => api.delete(`/merchant-patterns/${id}`).then(() => undefined),
  test: (description: string, amount?: number) =>
    api.post<PatternTestResult>('/merchant-patterns/test', { description, amount }).then(r => r.data),
  backfill: (id: number) =>
    api.post<{ updated: number }>(`/merchant-patterns/${id}/backfill`).then(r => r.data),
}

export default api
